/* eslint-disable no-param-reassign */
import Moment from 'moment';
import axios from 'axios';
import { message } from 'antd';
import { store } from 'store/index';
import { SET_NETPAY_AUTH_DATA } from 'store/reducers/auth';
import { SET_LAST_PAYMENT_DATA } from 'store/reducers/caja';
import { logger } from 'utils/debug';

const {
  REACT_APP_ENV: ENV,
  REACT_APP_STORE_ID: STORE_ID,
} = process.env;

const localhost = window.location.href.includes('localhost');
const prod = ENV === 'PRODUCTION' && !localhost;

const storeId = prod ? STORE_ID : '9194';
const SCHEME = 'https';
const HOST = prod ? 'suite.netpay.com.mx/' : 'api-154.api-netpay.com/';
const PATH = prod ? 'gateway/' : '';
const username = !prod ? 'Nacional' : 'smartPos';

export const baseURL = `${SCHEME}://${HOST}${PATH}`;

const {
  REACT_APP_SCHEME: SCHEME_GRP,
  REACT_APP_APIHOST: APIHOST_GRP,
  REACT_APP_PATH: PATH_GRP,
} = process.env;

export const baseURLGRP = `${SCHEME_GRP}://${APIHOST_GRP}${PATH_GRP}`;

const grant_type = 'password';
const password = 'netpay';

const auth = {
  username: 'trusted-app',
  password: 'secret',
};

const defaultOptions = {
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

const http = axios.create(defaultOptions);

const tokenURI = 'oauth-service/oauth/token';
const saleURI = 'integration-service/transactions/sale';
const cancelURI = 'integration-service/transactions/cancel';
const authURIs = [
  saleURI,
  cancelURI,
];

/**
 * POST oauth service to get token
 * @returns {Promise<string|boolean>}
 */
export const getToken = async () => {
  try {
    const formData = new FormData();
    formData.append('grant_type', grant_type);
    formData.append('username', username);
    formData.append('password', password);

    const response = await http.post(tokenURI, formData, {
      auth,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    store.dispatch({
      type: SET_NETPAY_AUTH_DATA,
      payload: response.data,
    });
    return response.data.access_token;
  } catch (err) {
    store.dispatch({
      type: SET_NETPAY_AUTH_DATA,
      payload: {},
    });
  }
  return false;
};

/**
 * POST oauth service to refresh token
 * @returns {Promise<string|boolean>}
 */
const refreshToken = async () => {
  try {
    const refresh_token = store.getState().auth?.netpay.refresh_token;
    const formData = new FormData();
    formData.append('grant_type', 'refresh_token');
    formData.append('refresh_token', refresh_token);
    formData.append('username', username);
    formData.append('password', password);

    const response = await http.post(tokenURI, formData, {
      auth,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    store.dispatch({
      type: SET_NETPAY_AUTH_DATA,
      payload: response.data,
    });
    return response.data.access_token;
  } catch (err) {
    store.dispatch({
      type: SET_NETPAY_AUTH_DATA,
      payload: {},
    });
  }
  return false;
};

const logError = async (_err = null) => {
  try {
    if (_err) {
      await axios.post(`${baseURLGRP}recaudacion/logs-frontend/`, {
        log: JSON.stringify(_err),
      });
    }
  } catch (err) {
    logger(err);
  }
};

/**
 * POST venta
 * @returns {Promise<boolean>}
 */
export const venta = async (_values) => {
  try {
    const values = { ..._values, storeId };
    await http.post('integration-service/transactions/sale', values);
    store.dispatch({
      type: SET_LAST_PAYMENT_DATA,
      payload: values.traceability.padrones.map((e) => ({ ...e, cargo: e.cargos.split(',')[0] })),
    });
    return true;
  } catch (err) {
    if (err.response?.data?.message) {
      message.info(err.response.data.message, 5);
    }
    logger(err);
    await logError(err.response?.data);
  }
  return false;
};

/**
 * POST Cancelacion de voucher
 * @returns {Promise<boolean>}
 */
export const cancelacionVoucher = async (_values) => {
  try {
    const values = { ..._values, storeId };
    await http.post(cancelURI, values);
    return true;
  } catch (err) {
    if (err.response?.data?.message) {
      message.info(err.response.data.message, 5);
    }
    logger(err);
    await logError(err.response?.data);
  }
  return false;
};

const requestInterceptor = async (options) => {
  const expires_at = store.getState().auth.netpay?.expires_at;
  let access_token = store.getState().auth.netpay?.access_token;
  if (!access_token && options.url !== 'oauth-service/oauth/token') {
    logger('getting token');
    access_token = await getToken();
  } else if (access_token && authURIs.includes(options.url)) {
    if (new Moment(expires_at).isAfter(new Moment())) {
      logger('refreshing token');
      access_token = await refreshToken();
    } else {
      logger('getting token');
      access_token = await getToken();
    }
  }
  if (access_token) {
    options.headers.Authorization = `Bearer ${access_token}`;
  }
  return options;
};

http.interceptors.request.use(requestInterceptor);

const responseErrorInterceptor = async (err) => {
  const inRefreshToken = err?.config?.url === 'oauth-service/oauth/token';
  if (err?.response?.status === 401 && !inRefreshToken) {
    const refreshedToken = await refreshToken();
    if (refreshedToken) {
      const config = { ...err.config };
      config.headers.Authorization = `Bearer ${refreshedToken}`;
      return axios.request(config);
    }
    store.dispatch({
      type: SET_NETPAY_AUTH_DATA,
      payload: {},
    });
  }

  return Promise.reject(err);
};

http.interceptors.response.use(null, responseErrorInterceptor);

export default http;
